import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Layout from './components/Layout/Layout';
import Dashboard from './components/Dashboard/Dashboard';
import HealthDashboard from './components/Health/HealthDashboard';
import TenantManagement from './components/Tenants/TenantManagement';
import AdminManagement from './components/Admins/AdminManagement';
import AnalystManagement from './components/Analysts/AnalystManagement';
import SuperAdminManagement from './components/Admins/SuperAdminManagement';
import QuestionManagement from './components/Questions/QuestionManagement';
import DraftQuestionManagement from './components/DraftQuestions/DraftQuestionManagement';
import UserManagement from './components/users/UserManagement';
import CreateUserForm from './components/users/CreateUserForm';
import RoleBasedDashboard from './components/RoleBasedDashboard';
import Settings from './components/Settings/Settings';
import Login from './components/Auth/Login';
import ResetPasswordPage from './pages/ResetPasswordPage';
import MyQuestions from './components/ContentGenerator/MyQuestions';
import Tags from './components/ContentGenerator/Tags';
import CreateQuestion from './components/ContentGenerator/CreateQuestion';
import BulkUpload from './components/ContentGenerator/BulkUpload';
import KitManagement from './components/KitManagement/KitManagement';
import WorkflowManagement from './components/Workflow/WorkflowManagement';
import TeamManagement from './components/Team/TeamManagement';
import Analytics from './components/Analytics/Analytics';
import QuestionsForReview from './components/ContentReviewer/QuestionsForReview';
import AssignedQuestions from './components/ContentReviewer/AssignedQuestions';
import CompletedReviews from './components/ContentReviewer/CompletedReviews';
import UUIDHelper from './components/UUIDHelper/UUIDHelper';
import TestConfigurationsPage from './pages/TestConfigurationsPage';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return isAuthenticated ? children : <Navigate to="/login" replace />;
};

// App Routes Component
const AppRoutes = () => {
  const { isAuthenticated } = useAuth();

  return (
    <Routes>
      {/* Public routes */}
      <Route
        path="/login"
        element={
          isAuthenticated ? <Navigate to="/dashboard" replace /> : <Login />
        }
      />
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      {/* Protected routes */}
      <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<RoleBasedDashboard />} />
        <Route path="health" element={<HealthDashboard />} />
        <Route path="tenants" element={<TenantManagement />} />
        <Route path="admins" element={<AdminManagement />} />
        <Route path="analysts" element={<AnalystManagement />} />
        <Route path="super-admins" element={<SuperAdminManagement />} />
        <Route path="users" element={<UserManagement />} />
        <Route path="users/create" element={<CreateUserForm />} />
        <Route path="questions" element={<QuestionManagement />} />
        <Route path="questions/create" element={<CreateQuestion />} />
        <Route path="questions/bulk-upload" element={<BulkUpload />} />
        <Route path="questions/pdf-to-mcq" element={<div>PDF to MCQ Page</div>} />
        <Route path="my-questions" element={<MyQuestions />} />
        <Route path="tags" element={<Tags />} />
        <Route path="uuid-helper" element={<UUIDHelper />} />
        <Route path="kits" element={<KitManagement />} />
        <Route path="workflow" element={<WorkflowManagement />} />
        <Route path="team" element={<TeamManagement />} />
        <Route path="analytics" element={<Analytics />} />
        <Route path="questions-for-review" element={<QuestionsForReview />} />
        <Route path="assigned-questions" element={<AssignedQuestions />} />
        <Route path="completed-reviews" element={<CompletedReviews />} />
        <Route path="draft-questions" element={<DraftQuestionManagement />} />
        <Route path="test-configurations" element={<TestConfigurationsPage />} />
        <Route path="settings" element={<Settings />} />
      </Route>
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <AuthProvider>
      <div className="min-h-screen bg-slate-50">
        <AppRoutes />
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#fff',
              color: '#363636',
              boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
              border: '1px solid #e2e8f0',
            },
            success: {
              duration: 3000,
              iconTheme: {
                primary: '#10b981',
                secondary: '#fff',
              },
            },
            error: {
              duration: 5000,
              iconTheme: {
                primary: '#ef4444',
                secondary: '#fff',
              },
            },
          }}
        />
      </div>
    </AuthProvider>
  );
}

export default App;
